import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { MagnifyingGlass, Users, Buildings, Briefcase, ListChecks, Calendar, Leaf } from "@phosphor-icons/react";
import { StatusBadge, Semaforo , EstadoBadge } from "../components/Semaforo";

export default function Search() {
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async (e) => {
    if (e) e.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get("/search", { params: { q } });
      setData(data);
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="search-page">
      <PageHeader title="Buscador inteligente" description="Encuentra comunidades, autoridades, proyectos, compromisos, reuniones y RCA en una sola consulta." />
      <div className="p-6 space-y-4">
        <form onSubmit={run} className="flex gap-2">
          <div className="relative flex-1 max-w-xl">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="search-input"
              autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder='“qué hacemos con la comunidad X”, “compromisos RCA pendientes”…'
              className="pl-9"
            />
          </div>
          <Button type="submit" className="bg-slate-900 text-white" data-testid="search-submit-btn" disabled={loading}>
            {loading ? "Buscando…" : "Buscar"}
          </Button>
        </form>

        {data && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ResultCard icon={Users} title="Comunidades" count={data.communities.length}>
              {data.communities.map(c => (
                <Link key={c.id} to={`/comunidades/${c.id}`} className="block py-1.5 text-sm hover:underline">{c.name}</Link>
              ))}
            </ResultCard>
            <ResultCard icon={Buildings} title="Autoridades" count={data.authorities.length}>
              {data.authorities.map(a => (
                <div key={a.id} className="py-1.5 flex items-center justify-between text-sm">
                  <span>{a.name}</span><StatusBadge>{a.entity_type}</StatusBadge>
                </div>
              ))}
            </ResultCard>
            <ResultCard icon={Briefcase} title="Proyectos" count={data.projects.length}>
              {data.projects.map(p => (
                <div key={p.id} className="py-1.5 flex items-center justify-between text-sm">
                  <span>{p.name}</span><EstadoBadge value={p.estado} />
                </div>
              ))}
            </ResultCard>
            <ResultCard icon={ListChecks} title="Compromisos" count={data.commitments.length}>
              {data.commitments.map(c => (
                <div key={c.id} className="py-1.5 flex items-center justify-between text-sm">
                  <span>{c.name}</span><Semaforo value={c.semaforo} />
                </div>
              ))}
            </ResultCard>
            <ResultCard icon={Calendar} title="Reuniones" count={data.meetings.length}>
              {data.meetings.map(m => (
                <div key={m.id} className="py-1.5 flex items-center justify-between text-sm">
                  <span>{m.asunto}</span><span className="text-xs text-slate-500">{m.fecha}</span>
                </div>
              ))}
            </ResultCard>
            <ResultCard icon={Leaf} title="RCA" count={data.rca.length}>
              {data.rca.map(r => (
                <div key={r.id} className="py-1.5 flex items-center justify-between text-sm">
                  <span>{r.nombre}</span><span className="text-xs font-mono text-slate-500">{r.codigo}</span>
                </div>
              ))}
            </ResultCard>
          </div>
        )}
      </div>
    </div>
  );
}

function ResultCard({ icon: Icon, title, count, children }) {
  return (
    <Card className="rounded-md shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2"><Icon size={16} /> {title}</span>
          <span className="text-xs font-normal text-slate-500">{count}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {count === 0 ? <div className="text-sm text-slate-500">Sin resultados.</div> : <div className="divide-y divide-slate-100">{children}</div>}
      </CardContent>
    </Card>
  );
}
